import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import Send from '@mui/icons-material/Send';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { PrimaryBtn } from 'components/UI/Buttons';
import React, { type FC, useRef, useState } from 'react';
import { usePostEmailMessageMutation } from 'services/api/messages';
import type { Client } from 'services/types/client';
import type { Message, MessageStatus } from 'services/types/message';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SHADOW_CARD,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

const ACCEPTED = '.jpg,.jpeg,.png,.mp4,.pdf';

const COMMON_EMOJIS = [
  ['😊', '👍', '🎉', '❤️', '🙏', '✨', '💪', '🤝'],
  ['👋', '🌟', '💯', '🔥', '👏', '🎯', '✅', '🚗'],
  ['📱', '💼', '📊', '📈', '🎁', '🛒', '💰', '📝'],
  ['🔔', '⏰', '📅', '🌈', '🚀', '🏆', '💡', '🔍'],
];

interface Props {
  client: Client;
  setConversation: React.Dispatch<React.SetStateAction<Message[]>>;
}

// Gmail-style compose box for email chat clients: shows the recipient, a tall multi-line body with
// emoji support, and multiple attachments — all sent as a single email (unlike WhatsApp, where each
// file and the text are separate messages).
const EmailComposer: FC<Props> = ({ client, setConversation }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  const [postEmailMessage, { isLoading }] = usePostEmailMessageMutation();

  // An email always needs text; attachments are optional.
  const canSend = text.trim().length > 0 && !isLoading;

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEmojiClick = (emoji: string) => {
    const textarea = textInputRef.current;
    const start = textarea?.selectionStart ?? text.length;
    const end = textarea?.selectionEnd ?? text.length;
    const newText = text.substring(0, start) + emoji + text.substring(end);
    setText(newText);
    setEmojiAnchorEl(null);
    setTimeout(() => {
      textarea?.focus();
      const newPosition = start + emoji.length;
      textarea?.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const handleSend = () => {
    if (!canSend) return;
    setConfirmOpen(false);
    setError(null);
    postEmailMessage({ clientId: client.id, text, files })
      .then((response) => {
        if ('data' in response && response.data) {
          setConversation((prev) => [
            ...(prev ?? []),
            ...response.data.map((message) => ({ ...message, status: 'sent' as MessageStatus })),
          ]);
          setText('');
          setFiles([]);
        }
        if ('error' in response && response.error) {
          setError('No se pudo enviar el correo. Revisa los archivos e inténtalo de nuevo.');
        }
      })
      .catch(() => setError('No se pudo enviar el correo. Revisa los archivos e inténtalo de nuevo.'));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 1, pt: 0.5 }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED}
        style={{ display: 'none' }}
        onChange={(e) => addFiles(e.target.files)}
      />
      <Box
        sx={{
          backgroundColor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
          p: 1.5,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={expanded ? 5 : 1}
          maxRows={expanded ? 16 : 1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setExpanded(true)}
          disabled={isLoading}
          placeholder="Escribe tu correo..."
          variant="standard"
          inputRef={textInputRef}
          slotProps={{ input: { disableUnderline: true } }}
          sx={{
            '& .MuiInputBase-root': { p: 0 },
            '& .MuiInputBase-input': { color: NAVY, caretColor: alpha(NAVY, 0.4), lineHeight: 1.6 },
          }}
        />

        {files.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {files.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                label={file.name}
                onDelete={() => removeFile(index)}
                deleteIcon={<CloseIcon />}
                disabled={isLoading}
                sx={{
                  backgroundColor: alpha(NAVY, 0.05),
                  color: NAVY,
                  borderRadius: RADIUS_ITEM,
                  maxWidth: 260,
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <PrimaryBtn
            variant="contained"
            onClick={() => setConfirmOpen(true)}
            disabled={!canSend}
            sx={{
              backgroundColor: YELLOW,
              color: NAVY,
              '&:hover': { backgroundColor: alpha(YELLOW, 0.9) },
              '&.Mui-disabled': { backgroundColor: alpha(YELLOW, 0.4), color: alpha(NAVY, 0.4) },
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} sx={{ color: NAVY }} />
            ) : (
              <>
                Enviar
                <Send fontSize="small" sx={{ ml: 1 }} />
              </>
            )}
          </PrimaryBtn>
          <Tooltip title="Adjuntar archivos" arrow>
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) } }}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Emojis" arrow>
            <span>
              <IconButton
                onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
                disabled={isLoading}
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) } }}
              >
                <InsertEmoticonIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={expanded ? 'Colapsar' : 'Expandir'} arrow>
            <span>
              <IconButton
                onClick={() => setExpanded((v) => !v)}
                disabled={isLoading}
                sx={{ color: alpha(NAVY, 0.55), '&:hover': { color: NAVY, backgroundColor: alpha(NAVY, 0.06) } }}
              >
                {expanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          {error && (
            <Typography variant="body2" sx={{ color: '#E7233B', ml: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </Box>

      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: -1, p: 1, borderRadius: RADIUS_CARD, boxShadow: SHADOW_CARD } } }}
      >
        {COMMON_EMOJIS.map((row) => (
          <Grid container key={`${row[0]}-${row[1]}`}>
            {row.map((emoji) => (
              <IconButton key={emoji} size="small" onClick={() => handleEmojiClick(emoji)} sx={{ fontSize: '1.5rem' }}>
                {emoji}
              </IconButton>
            ))}
          </Grid>
        ))}
      </Popover>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: RADIUS_CARD,
              backgroundColor: GLASS_BACKGROUND,
              backdropFilter: GLASS_BLUR,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: NAVY, pt: 3, px: 3, pb: 1 }}>Confirmar envío</DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.75), mb: files.length > 0 ? 1.5 : 0 }}>
            Se enviará un correo a <strong>{client.email || 'el cliente'}</strong>
            {files.length > 0
              ? ` con ${files.length} archivo${files.length === 1 ? '' : 's'} adjunto${files.length === 1 ? '' : 's'}.`
              : '.'}
          </Typography>
          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {files.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={file.name}
                  size="small"
                  sx={{ backgroundColor: alpha(NAVY, 0.05), color: NAVY, borderRadius: RADIUS_ITEM, maxWidth: 220 }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{ textTransform: 'none', color: alpha(NAVY, 0.6), borderRadius: RADIUS_ITEM }}
          >
            Cancelar
          </Button>
          <PrimaryBtn
            variant="contained"
            onClick={handleSend}
            disabled={isLoading}
            sx={{
              backgroundColor: YELLOW,
              color: NAVY,
              '&:hover': { backgroundColor: alpha(YELLOW, 0.9) },
              '&.Mui-disabled': { backgroundColor: alpha(YELLOW, 0.4), color: alpha(NAVY, 0.4) },
            }}
          >
            {isLoading ? <CircularProgress size={20} sx={{ color: NAVY }} /> : 'Enviar'}
          </PrimaryBtn>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailComposer;
